import React, { useEffect, useState } from "react";
import { Button, Spinner } from "@capacit-com/lara-law-shared-ui";
import { PlusIcon } from "lucide-react";
import useDocumentLink from "@/hooks/useDocumentLink";
import { buildDocumentLinkUrl } from "@/utils/domain";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { ReferenceTypePicker } from "@/components/document-link/ReferenceTypePicker";
import { buildDocumentReferenceRequest } from "@/lib/documentReferenceRequest";
import { logger } from "@/lib/logger";
import type { ConsolidationRenderContext } from "@/hooks/useLegislationViewerContent";

interface SelectionButtonProps {
  selectedDivIds: string[];
  destinationUniqueMatchText: string;
  destinationUniqueChunkText: string;
  buttonPosition: { top: string; left: string } | null;
  buttonRef: React.RefObject<HTMLDivElement | null>;
  currentSource?: string;
  currentDocId?: string;
  currentWorkspaceId?: string;
  /** Set when a consolidation is on screen, so the reference points back to it. */
  consolidationContext?: ConsolidationRenderContext | null;
  onClearSelection: () => void;
}

export const SelectionButton: React.FC<SelectionButtonProps> = ({
  selectedDivIds,
  destinationUniqueMatchText,
  destinationUniqueChunkText,
  buttonPosition,
  buttonRef,
  currentSource,
  currentDocId,
  currentWorkspaceId,
  consolidationContext = null,
  onClearSelection,
}) => {
  const { postTemplate } = useDocumentLink();
  const isRetsinformationLegislation =
    !currentWorkspaceId &&
    normalizeDataSource(currentSource) === DataSource.Retsinformation;
  const [isStatic, setIsStatic] = useState(false);

  useEffect(() => {
    setIsStatic(false);
  }, [selectedDivIds, currentSource]);

  if (selectedDivIds.length === 0 || !buttonPosition) return null;

  const handleCreateReference = async () => {
    const request = buildDocumentReferenceRequest(
      {
        documentId: currentDocId,
        source: currentSource,
        workspaceId: currentWorkspaceId,
        consolidation: consolidationContext,
      },
      isRetsinformationLegislation ? isStatic : true,
      {
        htmlIds: selectedDivIds,
        uniqueMatchText: destinationUniqueMatchText,
        uniqueChunkText: destinationUniqueChunkText,
      }
    );

    try {
      const response = await postTemplate.mutateAsync(request);
      const documentLinkUrl = buildDocumentLinkUrl(response);
      navigator.clipboard.writeText(documentLinkUrl);
      showSuccessToast("Reference oprettet og kopieret til udklipsholderen");
    } catch (error) {
      logger.error("Failed to create document link template", {
        operation: "createDocumentLinkTemplate",
        error,
        documentId: request.destinationDocumentId,
        source: currentSource,
        workspaceId: currentWorkspaceId,
        changeLaw: consolidationContext?.changeLaw,
      });
      showErrorToast("Referencen kunne ikke oprettes");
    } finally {
      onClearSelection();
    }
  };

  const containerClassName = isRetsinformationLegislation
    ? "fixed z-40 flex max-w-[min(100vw-2rem,22rem)] flex-col gap-3 rounded-lg border border-border bg-background p-3 shadow-lg"
    : "fixed z-40 flex flex-col";

  return (
    <div
      ref={buttonRef}
      className={containerClassName}
      style={{
        top: buttonPosition.top,
        left: buttonPosition.left,
      }}
    >
      {isRetsinformationLegislation ? (
        <ReferenceTypePicker
          value={isStatic ? "static" : "nonstatic"}
          onChange={(v) => setIsStatic(v === "static")}
          onConfirm={handleCreateReference}
          isPending={postTemplate.isPending}
          disabled={!currentSource || !currentDocId}
          idPrefix="selection-ref-type"
        />
      ) : (
        <Button
          className="shadow-md"
          size="lg"
          disabled={!currentSource || !currentDocId || postTemplate.isPending}
          onClick={handleCreateReference}
          id="create-reference-button"
        >
          {postTemplate.isPending ? (
            <Spinner size="small" className="mr-2" />
          ) : (
            <PlusIcon className="h-4 w-4 mr-2" />
          )}
          Opret reference
        </Button>
      )}
    </div>
  );
};
